/**
 * Discreet route pre-fetching.
 *
 * Given an unlocalized path, kicks off the dynamic import of the matching
 * route module so the JS chunk is already in the browser cache when the
 * user actually clicks. Idempotent: each path is only imported once.
 *
 * This only downloads the chunk — it does not execute any React render or
 * data fetching, and business logic is untouched.
 */
const PREFETCHERS: Record<string, () => Promise<unknown>> = {
  '/shop': () => import('@/pages/Shop'),
  '/cart': () => import('@/pages/Cart'),
  '/checkout': () => import('@/pages/Checkout'),
  '/loyalty': () => import('@/pages/Loyalty'),
  '/account': () => import('@/pages/Account'),
  '/account/login': () => import('@/pages/auth/Login'),
  '/account/signup': () => import('@/pages/auth/Signup'),
  '/search': () => import('@/pages/Search'),
  '/category/displays': () => import('@/pages/Category'),
  '/category/boosters': () => import('@/pages/Category'),
  '/category/lots': () => import('@/pages/Category'),
  '/category/case': () => import('@/pages/Category'),
  '/category/decks': () => import('@/pages/Category'),
  '/category/sleeves': () => import('@/pages/Category'),
  '/category/binder': () => import('@/pages/Category'),
  '/category/acrylic-protectors': () => import('@/pages/Category'),
  '/category/storage-boxes': () => import('@/pages/Category'),
};

const started = new Set<string>();

export function prefetchRoute(path: string): void {
  if (typeof window === 'undefined') return;
  // Strip locale prefix (e.g. /fr/shop → /shop)
  const stripped = path.replace(/^\/(fr|en-gb|de|es|it)(?=\/|$)/, '') || '/';
  const key = stripped in PREFETCHERS ? stripped : null;
  if (!key || started.has(key)) return;
  started.add(key);
  // Fire and forget — swallow errors so a preload never affects UX.
  PREFETCHERS[key]().catch(() => {});
}

/**
 * Idle-prefetch a few likely first-navigation targets after the app has painted.
 * Safe to call more than once.
 */
export function idlePrefetchCommonRoutes(): void {
  if (typeof window === 'undefined') return;
  const run = () => {
    prefetchRoute('/shop');
    prefetchRoute('/cart');
  };
  const w = window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
  if (typeof w.requestIdleCallback === 'function') {
    w.requestIdleCallback(run, { timeout: 2500 });
  } else {
    setTimeout(run, 1500);
  }
}
